if (!customElements.get('variant-selects')) {

  // MPC: one in-flight variant resolution per SECTION (E2) — the main and sticky pickers write the
  // same price nodes and the same hidden id, so they must share one generation counter.
  // Entries are deliberately never deleted: the retained generation is what keeps the counter
  // monotonic per section id for the page lifetime, so a picker rebuilt on the same section id
  // (quick view reopened) starts above every response still in flight from the previous one.
  const MPC_REQUESTS = new Map();

  /**
   *  @class
   *  @function VariantSelects
   */
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.sticky = this.dataset.sticky;
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.isDisabledFeature = this.dataset.isDisabled;
      this.prefetchCache = new Map();
      this.addEventListener('change', this.onVariantChange);
      this.other = Array.from(document.querySelectorAll('variant-selects')).filter((selector) => {
        return selector != this;
      });

      this.productWrapper = this.closest('.thb-product-detail');
      this.productSlider = this.productWrapper?.querySelector('.product-images');
      this.hideVariants = this.dataset.hideVariants === 'true';

      this.addEventListener('mousedown', (e) => {
        if (e.target.tagName === 'LABEL') {
          const scrollY = window.scrollY;
          requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: 'instant' }));
        }
      });
    }

    connectedCallback() {
      this.updateOptions();
      this.updateMasterId();
      // MPC: required-Size (E1) — paint the choose state on load; nothing is pre-selected.
      this.mpcSyncRequiredState();
      this.setDisabled();
      this.setImageSet();
      this.prefetchProductUrls();
      // MPC: required-Size (E1) — a browser restores form state AFTER the load event (history back
      // without bfcache) and fires no change event, so the size control can end up visibly chosen
      // while the hidden id is still blank. Re-check once the restore can have landed.
      this.mpcResyncHandler = (event) => {
        setTimeout(() => this.mpcResyncAfterRestore(), 0);
        // MPC: E1 — on a bfcache restore WebKit resets every autocomplete="off" control from a
        // posted task whose order against the timer above is not guaranteed, so look once more on
        // a later tick. Only for a real restore; a normal load never needs it.
        if (event && event.persisted) {
          clearTimeout(this.mpcResyncTimer);
          this.mpcResyncTimer = setTimeout(() => this.mpcResyncAfterRestore(), 120);
        }
      };
      window.addEventListener('pageshow', this.mpcResyncHandler);
      this.mpcResyncHandler();
    }

    // MPC: E2 — a picker that leaves the DOM (quick view closed, section re-rendered) must not let
    // its in-flight resolution paint into whatever now owns its section id.
    disconnectedCallback() {
      if (this.mpcController) this.mpcController.abort();
      // MPC: E1 — the restore resync is a window listener; it must not outlive this element.
      if (this.mpcResyncHandler) window.removeEventListener('pageshow', this.mpcResyncHandler);
      // MPC: E1 — nor may the extra bfcache tick fire into a detached picker.
      clearTimeout(this.mpcResyncTimer);
    }

    onVariantChange() {
      this.updateOptions();
      // MPC: required-Size (E1) — blank the id for the whole resolution window. Every button is
      // disabled below, but Enter in the quantity field submits the form regardless. Only sized
      // products: without a required option this is stock v8, which keeps the id until the new
      // one lands.
      if (this.mpcRequiresSize()) {
        this.mpcClearVariantInput();
        // MPC: v3 parity — the is-updating shimmer for the resolution window (v3 setButtonState).
        this.mpcSetUpdatingState(true);
      }
      this.toggleAddButton(true, '', false);
      this.removeErrorMessage();
      this.updateVariantText();
      this.setDisabled();

      this.renderVariantInfo();

      // MPC: E2 — a combined-listing switch can replace this section synchronously (prefetch
      // cache), and this element no longer owns it: it must not write into the new product's form.
      if (!this.isConnected) return;

      this.updateOther();
      dispatchCustomEvent('product:variant-change', {
        variant: this.currentVariant,
        sectionId: this.dataset.section,
        variantTitle: this.options.join(' / ')
      });
    }

    updateOptions() {
      this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = [];
      // MPC: required-Size (E1) — keep one entry per fieldset and use null for "not chosen";
      // the vendor line dereferenced input:checked and threw on load with Size unselected.
      this.allOptionsSelected = true;
      this.fieldsets.forEach((fieldset, i) => {
        let value = null;
        if (fieldset.querySelector('select')) {
          value = fieldset.querySelector('select').value || null;
        } else if (fieldset.querySelectorAll('input').length) {
          const checkedInput = fieldset.querySelector('input:checked');
          value = checkedInput ? checkedInput.value : null;
        } else {
          return;
        }
        this.options.push(value);
        if (value === null) {
          this.allOptionsSelected = false;
        } else {
          // MPC: the required-Size error clears as soon as that fieldset has a value.
          fieldset.classList.remove('has-error');
          const errorMessage = fieldset.querySelector('.variant-error');
          if (errorMessage) errorMessage.remove();
        }
      });
    }
    updateVariantText() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      fieldsets.forEach((item, i) => {
        let label = item.querySelector('.form__label__value');
        if (label) {
          // MPC: required-Size (E1) — an unchosen required option reads the prompt, never "null".
          if (this.options[i] == null) {
            if (item.hasAttribute('data-required')) {
              label.textContent = window.theme?.variantStrings?.selectSize || 'Select a size';
            }
          } else {
            label.innerHTML = this.options[i];
          }
        }
      });
    }
    updateMasterId() {
      // MPC: required-Size (E1) — an incomplete option set resolves to no variant, whatever the
      // server-rendered span says. On load that span names a default the shopper never chose.
      if (!this.allOptionsSelected) {
        this.currentVariant = null;
        return;
      }
      const span = this.querySelector(`#SelectedVariant-${this.dataset.section}`);
      if (!span) return;
      this.currentVariant = {
        id: parseInt(span.dataset.variantId, 10) || null,
        available: span.dataset.available === 'true',
        featured_media: span.dataset.featuredMediaId ? { id: parseInt(span.dataset.featuredMediaId, 10) } : null
      };
    }

    updateOther() {
      if (this.dataset.updateUrl === 'false') {
        return;
      }
      if (this.other.length) {
        let fieldsets = this.other[0].querySelectorAll('fieldset'),
          fieldsets_array = Array.from(fieldsets);
        this.options.forEach((option, i) => {
          // MPC: required-Size (E1/E3) — the twin may have no counterpart fieldset, and an unchosen
          // option is null, for which input[value='null'] matches nothing and the vendor line threw.
          const twin = fieldsets_array[i];
          if (!twin) return;
          if (option === null) {
            const twinSelect = twin.querySelector('select');
            if (twinSelect) {
              twinSelect.value = '';
            } else {
              twin.querySelectorAll('input').forEach((input) => {
                input.checked = false;
              });
            }
          } else if (twin.querySelector('select')) {
            twin.querySelector(`select`).value = option;
          } else if (twin.querySelectorAll('input').length) {
            // MPC: E3 — an option value carrying an apostrophe or a backslash made the vendor's
            // unescaped selector throw a SyntaxError, killing the twin sync mid-way.
            const target = twin.querySelector(`input[value="${CSS.escape(option)}"]`);
            if (target) target.checked = true;
          }
        });
        this.other[0].updateOptions();
        // MPC: E1 — the twin's #SelectedVariant span is never re-rendered, so updateMasterId would
        // give it the page-load default. Only a picker's own resolution may name a variant.
        this.other[0].currentVariant = null;
        this.other[0].updateVariantText();
        this.other[0].setDisabled();
        // MPC: required-Size (E1/E3) — keep the twin's required state in step with ours.
        this.other[0].mpcSyncRequiredState();
      }
    }
    updateMedia() {
      if (!this.currentVariant) return;
      if (!this.currentVariant.featured_media) return;
      // MPC: E2/E8 — the sticky picker lives in a modal that ModalDialog moves to document.body
      // (app.js connectedCallback), so it has no .thb-product-detail ancestor and no slider. The
      // vendor line below threw there, and that throw now lands in renderVariantInfo's catch and
      // would fail a perfectly good selection closed.
      if (!this.productSlider) return;

      let thumbnails = this.productWrapper.querySelector('.product-thumbnail-container');

      this.setActiveMedia(`#Slide-${this.dataset.section}-${this.currentVariant.featured_media.id}`, `#Thumb-${this.dataset.section}-${this.currentVariant.featured_media.id}`, this.productSlider, thumbnails);
    }
    setActiveMedia(mediaId, thumbId, productSlider, thumbnails) {
      let flkty = Flickity.data(productSlider),
        activeMedia = productSlider.querySelector(mediaId);
      if (flkty && this.hideVariants) {
        if (productSlider.querySelector('.product-images__slide.is-initial-selected')) {
          productSlider.querySelector('.product-images__slide.is-initial-selected').classList.remove('is-initial-selected');
        }
        [].forEach.call(productSlider.querySelectorAll('.product-images__slide-item--variant'), function (el) {
          el.classList.remove('is-active');
        });
        // MPC: E — read the supplied argument, not this.thumbnails, which VariantSelects never
        // assigns (only ProductSlider does), so this block never ran. Without it the thumbnails
        // keep every variant thumb marked is-active while the slides above are filtered to one,
        // and ProductSlider indexes its is-active thumbs by CELL index (setupEvents).
        if (thumbnails) {
          if (thumbnails.querySelector('.product-thumbnail.is-initial-selected')) {
            thumbnails.querySelector('.product-thumbnail.is-initial-selected').classList.remove('is-initial-selected');
          }
          [].forEach.call(thumbnails.querySelectorAll('.product-images__slide-item--variant'), function (el) {
            el.classList.remove('is-active');
          });
        }

        // MPC: E2/E8 — a variant whose featured media is not a rendered slide/thumb leaves these
        // null; the throw would reach renderVariantInfo's catch and fail the selection closed.
        if (activeMedia) {
          activeMedia.classList.add('is-active');
          activeMedia.classList.add('is-initial-selected');
        }

        this.setImageSetMedia();

        if (thumbnails) {
          let activeThumb = thumbnails.querySelector(thumbId);

          if (activeThumb) {
            activeThumb.classList.add('is-active');
            activeThumb.classList.add('is-initial-selected');
          }
        }

        productSlider.reInit(this.imageSetIndex);
        productSlider.selectCell(mediaId);

      } else if (flkty) {
        productSlider.selectCell(mediaId);
      }

    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateShareUrl() {
      const shareButton = document.getElementById(`Share-${this.dataset.section}`);
      if (!shareButton) return;
      shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
    }

    updateVariantInput(generation) {
      const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
      // MPC: E2 — each write below dispatches `change` synchronously, and a listener may start a
      // newer selection during that dispatch. Re-check before EVERY write (the installment form
      // comes first in document order, the purchase form second) so a superseded commit can never
      // reach the form that actually buys. Returns false when it gave up.
      for (const productForm of productForms) {
        if (generation != null && MPC_REQUESTS.get(this.dataset.section)?.generation !== generation) return false;
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      }
      return true;
    }

    updatePickupAvailability() {
      const pickUpAvailability = document.querySelector('.pickup-availability-wrapper');
      pickUpAvailability?.update(this.currentVariant);
    }

    removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;

      const productForm = section.querySelector('product-form');
      if (productForm) productForm.handleErrorMessage();
    }

    getSectionsToRender() {
      return [`price-${this.dataset.section}`, `price-${this.dataset.section}--sticky`, `product-image-${this.dataset.section}--sticky`, `inventory-${this.dataset.section}`, `sku-${this.dataset.section}`, `quantity-${this.dataset.section}`];
    }

    getSelectedValueIds() {
      return this.fieldsets.map((fieldset) => {
        if (fieldset.querySelector('select')) {
          return fieldset.querySelector('select').selectedOptions[0]?.dataset.valueId;
        }
        return fieldset.querySelector('input:checked')?.dataset.valueId;
      }).filter(Boolean);
    }

    renderVariantInfo() {
      // MPC: E2 — retire whatever is in flight for this section BEFORE any exit below. A switch to
      // a linked product, a partial set or a value-id mismatch must all supersede the previous
      // request; otherwise its response lands later and re-arms an id nobody chose.
      const generation = this.mpcInvalidateRequests();

      // Combined listings: check if selected options point to a different product
      const productUrl = this.getProductUrl();
      if (productUrl) {
        this.switchProduct(productUrl, generation);
        return;
      }

      // MPC: required-Size (E1) — never ask Shopify to resolve a partial option set.
      if (!this.allOptionsSelected) {
        this.mpcSyncRequiredState();
        return;
      }

      const ids = this.getSelectedValueIds();

      // MPC: required-Size (E1) — getSelectedValueIds ends in .filter(Boolean), so one missing
      // data-value-id would silently shorten the list into a partial set. Fail closed instead.
      if (ids.length !== this.options.length) {
        // MPC: say why we failed closed — a missing data-value-id is otherwise invisible.
        console.error('variant resolution: value-id mismatch', { ids: ids, options: this.options });
        this.mpcSetUnresolvedState();
        return;
      }

      const scrollY = window.scrollY;

      // MPC: E2 — hang this request's controller off the generation taken at the top, so nothing
      // but the latest request may paint and disconnectedCallback can abort it.
      const requestKey = this.dataset.section;
      const controller = new AbortController();
      const selection = this.options.slice();
      MPC_REQUESTS.set(requestKey, { generation, controller });
      this.mpcController = controller;

      fetch(`${this.dataset.url}?option_values=${ids.join(',')}&section_id=${this.dataset.section}`, { signal: controller.signal })
        .then((response) => response.text())
        .then((responseText) => {
          // MPC: E2 — this request has settled; drop its controller so the map keeps telling the
          // truth about what is still in flight.
          this.mpcSettleRequest(generation, controller);
          // MPC: E2 — a detached picker (quick view closed, section replaced) paints nothing: the
          // section id it holds may now belong to a different product's form.
          if (!this.isConnected) return;
          // MPC: E2 — a superseded response paints nothing at all.
          if (MPC_REQUESTS.get(requestKey)?.generation !== generation) return;
          // MPC: v3 parity — the resolution window is over; drop the is-updating shimmer.
          this.mpcSetUpdatingState(false);

          const html = new DOMParser().parseFromString(responseText, 'text/html');

          // 1. Resolve variant from hidden span
          const span = html.getElementById(`SelectedVariant-${this.dataset.section}`);

          // Combined listings server-side fallback: if response product differs from current
          if (span && span.dataset.productUrl && span.dataset.productUrl !== this.dataset.url) {
            this.switchProduct(span.dataset.productUrl, generation);
            return;
          }
          // MPC: required-Size (E1) — commit a variant id only if the server resolved the exact
          // combination we asked for. A mismatch is a wrong-size order; fail closed instead.
          if (span && parseInt(span.dataset.variantId, 10) && this.mpcResolvedMatches(span, selection)) {
            this.currentVariant = {
              id: parseInt(span.dataset.variantId, 10),
              available: span.dataset.available === 'true',
              featured_media: span.dataset.featuredMediaId ? { id: parseInt(span.dataset.featuredMediaId, 10) } : null
            };
          } else {
            this.currentVariant = null;
          }

          // 2. Sync data-available onto current DOM inputs (drives setDisabled)
          const sourceSelects = html.getElementById(`variant-selects-${this.dataset.section}`);
          if (sourceSelects) {
            sourceSelects.querySelectorAll('[data-value-id]').forEach((sourceEl) => {
              const vid = sourceEl.dataset.valueId;
              const avail = sourceEl.dataset.available;
              [this, ...this.other].forEach((el) => {
                const localEl = el.querySelector(`[data-value-id="${vid}"]`);
                if (localEl) localEl.dataset.available = avail;
              });
            });
          }

          // MPC: E — the loop below re-renders quantity-<section>, and the input inside it comes
          // back at the NEW variant's quantity_rule.min (product-add-to-cart.liquid:60), throwing
          // away whatever the shopper had typed. Read the live value and the minimum it was typed
          // against first. Both are null when show_quantity_selector is off.
          const quantityBefore = document.getElementById(`Quantity-${this.dataset.section}`);
          const typedQuantity = quantityBefore ? parseInt(quantityBefore.value, 10) : NaN;
          const previousMin = quantityBefore ? parseInt(quantityBefore.min, 10) : NaN;

          // 3. Update price, inventory, sku sections
          this.getSectionsToRender().forEach((id) => {
            const destination = document.getElementById(id);
            const source = html.getElementById(id);
            if (source && destination) destination.innerHTML = source.innerHTML;
            if (id.includes('price') && destination) destination.classList.remove('visibility-hidden');
          });

          // MPC: E — put the typed quantity back, but only a value the shopper actually moved off
          // the old minimum, and only onto a freshly rendered input (a different node: if the
          // response carried no quantity section, nothing was reset and nothing is touched). The
          // new variant's own rules decide the result - clamp into min/max, then snap DOWN to a
          // whole step from min - so the value written is one the cart will accept. Every lookup
          // is null-checked: a throw here lands in the catch below and fails a good selection
          // closed. No event is dispatched; validateQtyRules is called directly instead.
          // Only for a RESOLVED variant: an unresolved response carries another variant's rules.
          const quantityAfter = document.getElementById(`Quantity-${this.dataset.section}`);
          if (this.currentVariant && quantityAfter && quantityAfter !== quantityBefore && Number.isFinite(typedQuantity) && typedQuantity !== previousMin) {
            const min = parseInt(quantityAfter.min, 10) || 1;
            const max = parseInt(quantityAfter.max, 10);
            const step = parseInt(quantityAfter.step, 10) || 1;
            let quantity = Math.max(typedQuantity, min);
            if (Number.isFinite(max) && quantity > max) quantity = max;
            if (step > 1 && quantity > min) quantity = min + Math.floor((quantity - min) / step) * step;
            if (quantity < min) quantity = min;
            quantityAfter.value = quantity;
            const quantitySelector = quantityAfter.closest('quantity-selector');
            if (quantitySelector && typeof quantitySelector.validateQtyRules === 'function') quantitySelector.validateQtyRules();
          }

          // Update Pickup Availability
          this.updatePickupAvailability();

          // Variant resolved -> product select standard event
          if (this.currentVariant) {
            publishStandardEvent('ProductSelectEvent', {
              product: this.dataset.productId ? { id: this.dataset.productId } : undefined,
              promise: Promise.resolve({ variant: { id: this.currentVariant.id } })
            }, this);
          }

          // 4. Apply updated state
          if (!this.currentVariant) {
            this.toggleAddButton(true, '', true);
            this.setUnavailable();
            window.scrollTo({ top: scrollY, behavior: 'instant' });
            return;
          }
          this.setDisabled();
          if (!this.updateVariantInput(generation)) return;
          // MPC: E2 — the commit loop dispatched `change`; a listener may have superseded this
          // resolution or torn the picker down. Re-check before re-arming the purchase controls.
          if (MPC_REQUESTS.get(requestKey)?.generation !== generation || !this.isConnected) return;
          // MPC: required-Size (E1) — the accelerated checkout button is safe again: this form now
          // holds a variant id the shopper actually chose. A sold-out one stays hidden: that
          // button checks out on its own and cannot be refused by the submit guard.
          this.mpcToggleDynamicCheckout(this.currentVariant.available);
          if (this.updateUrl) this.updateURL();
          this.toggleAddButton(!this.currentVariant.available, window.theme.variantStrings.soldOut);
          this.updateMedia();
          window.scrollTo({ top: scrollY, behavior: 'instant' });
        })
        .catch((error) => {
          // MPC: E2 — settled, whatever the outcome.
          this.mpcSettleRequest(generation, controller);
          // MPC: E2 — aborted and superseded requests are silent; a live failure fails closed
          // rather than leaving a stale id behind an enabled button. v8 ships no catch at all.
          if (!this.isConnected) return;
          if (error.name === 'AbortError') return;
          if (MPC_REQUESTS.get(requestKey)?.generation !== generation) return;
          console.error('variant resolution failed', error);
          this.mpcSetUnresolvedState();
        });
    }

    toggleAddButton(disable = true, text = false, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) return;

      const productTemplate = productForm.closest('.product-form').getAttribute('template');
      // MPC: cleanup 2.4 — scope by form association; the vendor query was page-global, so a
      // quick-view drawer repainted the product page's buttons underneath it.
      const submitButtons = this.mpcButtons();

      if (!submitButtons) return;

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');

        if (!submitButtonText) return;

        if (disable) {
          submitButton.setAttribute('disabled', 'disabled');
          if (text) {
            submitButtonText.dataset.content = text;
            // MPC: v3 parity — a named disabled state (sold out) is the is-unavailable style.
            submitButton.classList.remove('is-updating');
            submitButton.classList.add('is-unavailable');
          }
        } else {
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading');
          // MPC: v3 parity — a buyable variant clears every not-buyable class, including the
          // sold-out one setUnavailable adds (the vendor left it on, greying an enabled button).
          submitButton.classList.remove('is-updating', 'is-unavailable', 'sold-out');

          if (productTemplate?.includes('pre-order')) {
            submitButtonText.dataset.content = window.theme.variantStrings.preOrder;
          } else {
            submitButtonText.dataset.content = window.theme.variantStrings.addToCart;
          }
        }
      });

      if (!modifyClass) return;
    }

    setUnavailable() {
      // MPC: cleanup 2.4 — scope by form association, as toggleAddButton now does.
      const submitButtons = this.mpcButtons();
      const price = document.getElementById(`price-${this.dataset.section}`);
      const price_fixed = document.getElementById(`price-${this.dataset.section}--sticky`);

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');
        // MPC: the vendor tested the button, not the span it then dereferenced. The failure path
        // (mpcSetUnresolvedState) now runs through here, so the typo would throw inside a .catch.
        if (!submitButtonText) return;
        submitButtonText.dataset.content = window.theme.variantStrings.unavailable;
        submitButton.classList.add('sold-out');
        // MPC: v3 parity — the unavailable state carries its own class (v3 setButtonState).
        submitButton.classList.remove('is-updating');
        submitButton.classList.add('is-unavailable');
      });
      if (price) price.classList.add('visibility-hidden');
      if (price_fixed) price_fixed.classList.add('visibility-hidden');
    }

    // ---- MPC: required-Size (port item E1) ---------------------------------------------------
    // The form is the only purchase path. The sticky button sits OUTSIDE the form and is bound to
    // it by its form attribute, so every button query is scoped by form association, never by the
    // document: a quick-view drawer opened over a product page holds a second product form.

    // MPC: the form this picker's section owns.
    mpcOwnerForm() {
      return document.getElementById(`product-form-${this.dataset.section}`);
    }

    // MPC: E2 — abort this section's in-flight resolution and take the next generation. Called at
    // the top of renderVariantInfo, so every exit from it retires the previous request, and reused
    // by the fetch path itself. Entries are never deleted (see MPC_REQUESTS above).
    mpcInvalidateRequests() {
      const key = this.dataset.section;
      const previous = MPC_REQUESTS.get(key);
      if (previous && previous.controller) previous.controller.abort();
      const generation = (previous ? previous.generation : 0) + 1;
      MPC_REQUESTS.set(key, { generation, controller: null });
      return generation;
    }

    // MPC: E2 — a request has settled (resolved, failed or aborted); forget its controller. Both
    // identity guards matter: an aborted request settles AFTER the request that superseded it
    // started, so it must never clear the newer one's controller.
    mpcSettleRequest(generation, controller) {
      const entry = MPC_REQUESTS.get(this.dataset.section);
      if (entry && entry.generation === generation) entry.controller = null;
      if (this.mpcController === controller) this.mpcController = null;
    }

    // MPC: required-Size (E1) — a browser can move the controls and the hidden id out of step
    // without firing a single change event, in BOTH directions: a form-state restore re-selects a
    // size over a blank id (resolve it), and a WebKit bfcache restore resets the autocomplete="off"
    // radios while the committed id survives (retract it). Test the two against each other rather
    // than the id alone, and let the normal entry point repaint whichever way it went.
    mpcResyncAfterRestore() {
      if (!this.isConnected || !this.mpcRequiresSize()) return;
      const form = this.mpcOwnerForm();
      const input = form ? form.querySelector('input[name="id"]') : null;
      if (!input) return;
      if (MPC_REQUESTS.get(this.dataset.section)?.controller) return;
      const chosen = Array.from(this.querySelectorAll('fieldset[data-required="true"]')).every((fieldset) => {
        const select = fieldset.querySelector('select');
        return select ? !!select.value : !!fieldset.querySelector('input:checked');
      });
      if (chosen !== !!input.value) this.onVariantChange();
    }

    // MPC: the submit buttons associated with that form (main + sticky).
    mpcButtons() {
      const form = this.mpcOwnerForm();
      if (!form) return [];
      return Array.from(document.querySelectorAll('.single-add-to-cart-button')).filter((button) => button.form === form);
    }

    // MPC: blank every id this picker is allowed to write — the mirror of updateVariantInput.
    // v3 blanked without dispatching change (themes/live/assets/product.js:385-391); so do we.
    mpcClearVariantInput() {
      const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (input) input.value = '';
      });
      // MPC: no committed id means no accelerated checkout either — it bypasses the submit guard.
      this.mpcToggleDynamicCheckout(false);
    }

    // MPC: does this picker carry a required option? Only then may we touch the sized-product UI;
    // a product without a Size option must behave exactly as stock v8.
    mpcRequiresSize() {
      return !!this.querySelector('fieldset[data-required="true"]');
    }

    // MPC: the dynamic-checkout ("Buy it now") wrapper this section owns. It submits through
    // Shopify's own code path, so onSubmitHandler can never refuse it — hide it instead.
    mpcToggleDynamicCheckout(show) {
      if (!this.mpcRequiresSize()) return;
      const wrapper = document.getElementById(`DynamicCheckout-${this.dataset.section}`);
      if (wrapper) wrapper.hidden = !show;
    }

    // MPC: v3 parity — the in-flight shimmer (themes/live/assets/product.js:460-505).
    mpcSetUpdatingState(active) {
      this.mpcButtons().forEach((button) => {
        button.classList.toggle('is-updating', active);
        if (active) button.classList.remove('is-unavailable');
      });
    }

    // MPC: accept a resolved variant only when its option values equal the ones we requested.
    mpcResolvedMatches(span, selection) {
      let resolved = null;
      try {
        resolved = JSON.parse(span.dataset.variantOptions || 'null');
      } catch (error) {
        return false;
      }
      if (!Array.isArray(resolved) || resolved.length !== selection.length) return false;
      return selection.every((value, index) => value === resolved[index]);
    }

    // MPC: nothing chosen yet — the button is enabled and reads "Select Size"; the submit handler
    // is what refuses, so the shopper gets an explanation instead of a dead control.
    mpcSetChooseState() {
      const label = window.theme?.variantStrings?.selectOptions || 'Select Size';
      this.mpcButtons().forEach((button) => {
        button.classList.remove('loading', 'sold-out', 'is-updating', 'is-unavailable');
        button.removeAttribute('disabled');
        const buttonText = button.querySelector('.single-add-to-cart-button--text');
        if (buttonText) buttonText.dataset.content = label;
      });
      document.getElementById(`price-${this.dataset.section}`)?.classList.remove('visibility-hidden');
      document.getElementById(`price-${this.dataset.section}--sticky`)?.classList.remove('visibility-hidden');
    }

    // MPC: resolution failed or returned something we did not ask for — fail closed.
    mpcSetUnresolvedState() {
      this.mpcClearVariantInput();
      // MPC: no variant is committed any more — nothing downstream may read the previous one.
      this.currentVariant = null;
      this.mpcButtons().forEach((button) => {
        button.classList.remove('loading');
        button.setAttribute('disabled', 'disabled');
      });
      // MPC: the vendor's own Unavailable paint — label, sold-out class, hidden price nodes.
      this.setUnavailable();
      // MPC: E1 — the only other exits from this state are a `change` event and a reload, so a
      // Size with a single value, or clicking the size already chosen, is a dead end. Retry on the
      // next click, DEFERRED to a later task: a radio's click bubbles BEFORE its change fires, so a
      // synchronous retry would race the change path into a double fetch. Deferred, the change path
      // goes first and the in-flight check below sees its request and no-ops.
      if (!this.mpcRetryPending) {
        this.mpcRetryPending = true;
        this.addEventListener('click', () => {
          this.mpcRetryPending = false;
          setTimeout(() => {
            if (!this.isConnected) return;
            if (MPC_REQUESTS.get(this.dataset.section)?.controller) return;
            // MPC: E1 — a sized picker retries through the resync, which compares the live controls
            // against the hidden id. A picker with no required option has nothing to compare and
            // mpcResyncAfterRestore self-gates on mpcRequiresSize, so retry the resolution itself —
            // otherwise a failed fetch leaves a buyable product dead until some OTHER value is clicked.
            if (this.mpcRequiresSize()) this.mpcResyncAfterRestore();
            else this.onVariantChange();
          }, 0);
        }, { once: true });
      }
    }

    // MPC: the single entry point for "this option set is incomplete". Complete sets are left to
    // the vendor's own flow untouched, so a product without a Size option behaves exactly as stock.
    mpcSyncRequiredState() {
      // MPC: required-Size (E1) — make the invariant above true at the gate, not by implication:
      // a picker with no required option is never touched by this path at all.
      if (!this.mpcRequiresSize()) return;
      if (this.allOptionsSelected) return;
      this.mpcClearVariantInput();
      this.updateVariantText();
      this.mpcSetChooseState();
    }

    setDisabled() {
      if (this.isDisabledFeature != 'true') return;

      this.fieldsets.forEach((fieldset) => {
        if (fieldset.querySelector('select')) {
          fieldset.querySelectorAll('option').forEach((opt) => {
            // MPC: required-Size (E1) — never re-enable the blank "Select Size" placeholder.
            if (!opt.dataset.valueId) return;
            opt.disabled = opt.dataset.available === 'false';
          });
        } else {
          fieldset.querySelectorAll('input').forEach((input) => {
            input.classList.toggle('is-disabled', input.dataset.available === 'false');
          });
        }
      });
      return true;
    }

    setImageSet() {
      if (!this.productSlider) return;

      let dataSetEl = this.productSlider.querySelector('[data-set-name]');
      if (dataSetEl) {
        this.imageSetName = dataSetEl.dataset.setName;
        this.imageSetIndex = this.querySelector('.product-form__input[data-handle="' + this.imageSetName + '"]').dataset.index;
        this.dataset.imageSetIndex = this.imageSetIndex;

        this.setImageSetMedia();
      }
    }
    prefetchProductUrls() {
      var productUrlElements = this.querySelectorAll('[data-product-url]');
      var currentUrl = this.dataset.url;
      var sectionId = this.dataset.section;
      var cache = this.prefetchCache;

      productUrlElements.forEach(function (el) {
        var productUrl = el.dataset.productUrl;
        if (!productUrl || productUrl === currentUrl) return;
        if (cache.has(productUrl)) return;

        var hoverTarget;
        if (el.tagName === 'OPTION') {
          hoverTarget = el.closest('select');
        } else {
          hoverTarget = el.parentElement.querySelector('label[for="' + el.id + '"]') || el;
        }

        hoverTarget.addEventListener('mouseenter', function () {
          if (cache.has(productUrl)) return;
          fetch(productUrl + '?section_id=' + sectionId)
            .then(function (response) { return response.text(); })
            .then(function (html) {
              cache.set(productUrl, html);
            })
            .catch(function () { });
        }, { once: true });
      });
    }

    getProductUrl() {
      var currentUrl = this.dataset.url;

      for (var i = 0; i < this.fieldsets.length; i++) {
        var fieldset = this.fieldsets[i];
        var productUrl = null;

        if (fieldset.querySelector('select')) {
          var selectedOption = fieldset.querySelector('select').selectedOptions[0];
          productUrl = selectedOption ? selectedOption.dataset.productUrl : null;
        } else {
          var checkedInput = fieldset.querySelector('input:checked');
          productUrl = checkedInput ? checkedInput.dataset.productUrl : null;
        }

        if (productUrl && productUrl !== currentUrl) {
          return productUrl;
        }
      }
      return null;
    }

    switchProduct(productUrl, generation) {
      var sectionId = this.dataset.section;
      var scrollY = window.scrollY;
      var self = this;

      // MPC: E2 — a switch carries the generation of the selection that asked for it. Once a newer
      // selection has superseded it, it may neither fail a freshly committed id closed nor repaint
      // a section this picker no longer owns.
      var stillCurrent = function () {
        return self.isConnected && MPC_REQUESTS.get(sectionId)?.generation === generation;
      };

      // MPC: E2 — onVariantChange has already blanked the id and started the shimmer. A switch
      // that never lands must fail closed instead of leaving a dead, shimmering button forever.
      var failClosed = function () {
        if (stillCurrent()) self.mpcSetUnresolvedState();
      };

      var applyHtml = function (responseText) {
        if (!stillCurrent()) return;
        var html = new DOMParser().parseFromString(responseText, 'text/html');
        var newSection = html.getElementById('shopify-section-' + sectionId);

        if (!newSection) {
          failClosed();
          return;
        }

        var currentSection = document.getElementById('shopify-section-' + sectionId);
        if (!currentSection) {
          failClosed();
          return;
        }

        currentSection.innerHTML = newSection.innerHTML;
        window.history.replaceState({}, '', productUrl);
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      };

      if (this.prefetchCache.has(productUrl)) {
        applyHtml(this.prefetchCache.get(productUrl));
        return;
      }

      fetch(productUrl + '?section_id=' + sectionId)
        .then(function (response) { return response.text(); })
        .then(function (responseText) {
          applyHtml(responseText);
        })
        // MPC: E2 — a switch that never lands must fail closed, not leave a dead shimmering button.
        .catch(failClosed);
    }

    setImageSetMedia() {
      if (!this.imageSetIndex) {
        return;
      }

      const optionPosition = parseInt(this.imageSetIndex.replace('option', ''), 10) - 1;
      const fieldset = this.fieldsets[optionPosition];
      if (!fieldset) return;
      const select = fieldset.querySelector('select');
      const checked = fieldset.querySelector('input:checked');
      let setValue;
      if (select && select.selectedOptions[0]) {
        setValue = select.selectedOptions[0].dataset.handle;
      } else if (checked) {
        setValue = checked.dataset.handle;
      }
      if (!setValue) return;
      let group = this.imageSetName + '_' + setValue;
      let selected_set_images = this.productWrapper.querySelectorAll(`.product-images__slide[data-set-name="${this.imageSetName}"]`),
        selected_set_thumbs = this.productWrapper.querySelectorAll(`.product-thumbnail[data-set-name="${this.imageSetName}"]`);
      if (this.hideVariants) {
        // Product images
        this.productWrapper.querySelectorAll('.product-images__slide').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_images.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });

        // Product thumbnails
        this.productWrapper.querySelectorAll('.product-thumbnail').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_thumbs.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });
      }

    }

    createAvailableOptionsTree(variant_data, selected_options) {
      // Reduce variant array into option availability tree
      return variant_data.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = {
              value: variant[index],
              isUnavailable: true
            };
            options[index].push(entry);
          }

          // Check how many selected option values match a variant
          const countVariantOptionsThatMatchCurrent = selected_options.reduce((count, {
            value,
            index
          }) => {
            return variant[index] === value ? count + 1 : count;
          }, 0);

          // Only enable an option if an available variant matches all but one current selected value
          if (countVariantOptionsThatMatchCurrent >= selected_options.length - 1) {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }

          // Make sure if a variant is unavailable, disable currently selected option
          if ((!this.currentVariant || !this.currentVariant.available) && selected_options.find((option) => option.value === entry.value && index === option.index)) {
            entry.isUnavailable = true;
          }

          // First option is always enabled
          if (index === 'option1') {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }
        });

        return options;
      }, {
        option1: [],
        option2: [],
        option3: []
      });
    }
  }
  customElements.define('variant-selects', VariantSelects);

  /**
   *  @class
   *  @function VariantRadios
   */
  class VariantRadios extends VariantSelects {
    constructor() {
      super();
    }

    updateOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = fieldsets.map((fieldset) => {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
      });
    }
  }

  customElements.define('variant-radios', VariantRadios);
}

if (!customElements.get('product-slider')) {
  /**
   *  @class
   *  @function ProductSlider
   */
  class ProductSlider extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', this.setupProductGallery);
    }
    connectedCallback() {
      this.product_container = this.closest('.thb-product-detail');
      this.quickView = this.closest('#Product-Drawer-Content');
      this.thumbnail_container = this.product_container.querySelector('.product-thumbnail-container');
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');
      this.progress_bar = this.product_container.querySelector('.flickity-progress--bar'),

        this.setOptions();
      // Defer Flickity init to next frame — avoids forced reflow during DOM parsing
      requestAnimationFrame(() => this.init());
    }
    setOptions() {
      this.hide_variants = this.dataset.hideVariants == 'true';
      this.thumbnails = this.thumbnail_container?.querySelectorAll('.product-thumbnail');
      this.prev_button = this.querySelector('.flickity-prev');
      this.next_button = this.querySelector('.flickity-next');
      this.images = this.querySelectorAll('img');
      this.options = {
        wrapAround: true,
        pageDots: true,
        contain: true,
        cellAlign: this.quickView ? 'left' : 'right',
        adaptiveHeight: !this.quickView,
        initialIndex: '.is-initial-selected',
        prevNextButtons: false,
        rightToLeft: document.dir === 'rtl',
        fade: false,
        cellSelector: '.product-images__slide.is-active',
        on: {
          ready: () => {
            this.images.forEach((img) => {
              img.addEventListener('lazyloaded', () => {
                this.flkty.resize();
              });
            });
          },
          dragStart: () => {
            this.flkty.slider.childNodes.forEach(slide => slide.style.pointerEvents = "none");
          },
          dragEnd: () => {
            this.flkty.slider.childNodes.forEach(slide => slide.style.pointerEvents = "all");
          }
        }
      };
      if (this.quickView) {
        this.options.wrapAround = false;
      }
      if (this.progress_bar) {
        this.options.on.scroll = (progress) => {
          progress = Math.max(0, Math.min(1, progress));
          this.progress_bar.style.width = progress * 100 + '%';
        };
      }
    }
    init() {
      this.flkty = new Flickity(this, this.options);

      this.selectedIndex = this.flkty.selectedIndex;

      // Setup Events
      this.setupEvents();

      // Start Gallery
      this.setupProductGallery();
    }
    reInit(imageSetIndex) {
      this.flkty.destroy();

      this.setOptions();

      this.flkty = new Flickity(this, this.options);

      // Setup Events
      this.setupEvents();

      this.selectedIndex = this.flkty.selectedIndex;
    }
    setupEvents() {
      const _this = this;
      if (this.prev_button) {
        let prev = this.prev_button.cloneNode(true);
        this.prev_button.parentNode.append(prev);
        this.prev_button.remove();
        prev.addEventListener('click', (event) => {
          document.dir === 'rtl' ? this.flkty.next() : this.flkty.previous();
        });
        prev.addEventListener('keyup', (event) => {
          document.dir === 'rtl' ? this.flkty.next() : this.flkty.previous();
          event.preventDefault();
        });
      }
      if (this.next_button) {
        let next = this.next_button.cloneNode(true);
        this.next_button.parentNode.append(next);
        this.next_button.remove();
        next.addEventListener('click', (event) => {
          document.dir === 'rtl' ? this.flkty.previous() : this.flkty.next();
        });
        next.addEventListener('keyup', (event) => {
          document.dir === 'rtl' ? this.flkty.previous() : this.flkty.next();
          event.preventDefault();
        });
      }
      this.video_containers.forEach((container) => {
        container.querySelector('button').addEventListener('click', function () {
          container.setAttribute('hidden', '');
        });
      });
      this.flkty.on('settle', function (index) {
        _this.selectedIndex = index;
      });
      this.flkty.on('change', (index) => {

        let previous_slide = this.flkty.cells[_this.selectedIndex].element,
          previous_media = previous_slide.querySelector('.product-single__media');

        if (this.thumbnails) {
          let active_thumbs = Array.from(this.thumbnails).filter(element => element.classList.contains('is-active')),
            active_thumb = active_thumbs[index] ? active_thumbs[index] : active_thumbs[0];

          this.thumbnails.forEach((item, i) => {
            item.classList.remove('is-initial-selected');
          });
          active_thumb.classList.add('is-initial-selected');

          requestAnimationFrame(() => {
            if (active_thumb.offsetParent === null) {
              return;
            }
            const windowHalfHeight = active_thumb.offsetParent.clientHeight / 2,
              windowHalfWidth = active_thumb.offsetParent.clientWidth / 2;
            active_thumb.parentElement.scrollTo({
              left: active_thumb.offsetLeft - windowHalfWidth + active_thumb.clientWidth / 2,
              top: active_thumb.offsetTop - windowHalfHeight + active_thumb.clientHeight / 2,
              behavior: 'smooth'
            });
          });
        }


        // Stop previous video
        if (previous_media.classList.contains('product-single__media-external-video')) {
          if (previous_media.dataset.provider === 'youtube') {
            previous_media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
              event: "command",
              func: "pauseVideo",
              args: ""
            }), "*");
          } else if (previous_media.dataset.provider === 'vimeo') {
            previous_media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
              method: "pause"
            }), "*");
          }
          previous_media.querySelector('.product-single__media-external-video--play').removeAttribute('hidden');
        } else if (previous_media.classList.contains('product-single__media-native-video')) {
          previous_media.querySelector('video').pause();
        }

      });

      if (this.thumbnails && !this.thumbnail_container.dataset.init) {
        setTimeout(() => {
          let active_thumbs = this.hide_variants ?
            Array.from(this.thumbnails).filter(element => element.classList.contains('is-active')) :
            Array.from(this.thumbnails);
          active_thumbs.forEach((thumbnail, index) => {
            thumbnail.addEventListener('click', () => {
              this.thumbnailClick(thumbnail, index);
            });
          });
          this.thumbnail_container.addEventListener('click', (e) => {
            let count = Math.min(Math.max(this.thumbnail_container.querySelectorAll('.is-active').length, 1), 4);
            this.thumbnail_container.style.setProperty('--thumbnail-width', (count * 63) + 19 + 'px');
            if (!this.thumbnail_container.classList.contains('extended')) {
              this.thumbnail_container.classList.add('extended');
            } else {
              if (e.target.nodeName == 'BUTTON') {
                this.thumbnail_container.classList.remove('extended');
              }
              e.preventDefault();
            }
            if (!this.thumbnail_container.classList.contains('extended')) {
              let active_thumb = this.thumbnail_container.querySelector('.is-initial-selected');
              setTimeout(() => {
                active_thumb.parentElement.scrollTo({
                  left: Math.max(active_thumb.offsetLeft, 0),
                  top: 0,
                  behavior: 'smooth'
                });
              }, 300);
            }
          });
          this.thumbnail_container.dataset.init = true;
        });
      }
    }
    thumbnailClick(thumbnail, index) {
      [].forEach.call(this.thumbnails, function (el) {
        el.classList.remove('is-initial-selected');
      });
      thumbnail.classList.add('is-initial-selected');
      this.flkty.select(index);
    }
    setDraggable(draggable) {
      this.flkty.options.draggable = draggable;
      this.flkty.updateDraggable();
    }
    selectCell(mediaId) {
      this.flkty.selectCell(mediaId);
    }
    setupProductGallery() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }
      this.setEventListeners();

      document.addEventListener('product:variant-change', (e) => {
        if (e.detail && e.detail.variantTitle && e.detail.variantTitle !== 'Default Title') {
          this.dataset.variantTitle = e.detail.variantTitle;
        }
      });
    }
    buildItems() {
      this.activeImages = Array.from(this.querySelectorAll('.product-images__slide.is-active .product-single__media-image'));

      return this.activeImages.map((item) => {
        let index = [].indexOf.call(item.parentNode.parentNode.children, item.parentNode);

        let activelink = item.querySelector('.product-single__media-zoom');

        activelink.dataset.index = index;
        return {
          src: activelink.getAttribute('href'),
          msrc: activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h,
          thumbSrc: activelink.dataset.thumbSrc || activelink.dataset.msrc
        };
      });
    }
    setEventListeners() {
      this.links = this.querySelectorAll('.product-single__media-zoom');
      this.pswpElement = document.querySelectorAll('.pswp')[0];
      this.pswpOptions = {
        maxSpreadZoom: 2,
        loop: true,
        allowPanToNext: true,
        closeOnScroll: false,
        showHideOpacity: false,
        arrowKeys: true,
        history: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: true,
        arrowEl: true,
        preloaderEl: true,
        getThumbBoundsFn: () => {
          const thumbnail = this.querySelector('.product-images__slide.is-selected'),
            pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();
          return {
            x: rect.left,
            y: rect.top + pageYScroll,
            w: rect.width
          };
        }
      };


      this.links.forEach((link => {
        link.addEventListener('click', (e) => this.zoomClick(e, link));
      }));
    }
    buildPswpThumbnails(items) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      container.innerHTML = '';
      if (this.dataset.lightboxThumbnails === 'false' || items.length <= 1) {
        container.parentElement.style.display = 'none';
        return;
      }
      container.parentElement.style.display = '';
      items.forEach((item, index) => {
        const thumb = document.createElement('div');
        thumb.className = 'pswp__thumb';
        thumb.dataset.index = index;
        const img = document.createElement('img');
        img.src = item.thumbSrc;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
        container.appendChild(thumb);
      });
    }
    updatePswpProductInfo() {
      const infoEl = this.pswpElement.querySelector('.pswp__product-info');
      if (!infoEl) return;
      if (this.dataset.lightboxProductInfo === 'false') {
        infoEl.style.display = 'none';
        return;
      }
      infoEl.style.display = '';
      const titleEl = infoEl.querySelector('.pswp__product-title');
      const variantEl = infoEl.querySelector('.pswp__variant-title');
      if (titleEl) titleEl.textContent = this.dataset.productTitle || '';
      if (variantEl) variantEl.textContent = this.dataset.variantTitle || '';
    }
    syncPswpThumbnails(index) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      const thumbs = container.querySelectorAll('.pswp__thumb');
      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
      });
      const activeThumb = container.querySelector('.pswp__thumb.is-active');
      if (activeThumb) {
        activeThumb.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
      }
    }
    zoomClick(e, link) {
      this.items = this.buildItems();
      this.pswpOptions.index = parseInt(link.dataset.index, 10);
      if (typeof PhotoSwipe !== 'undefined') {
        this.buildPswpThumbnails(this.items);
        this.updatePswpProductInfo();

        // Prevent touch/mouse events on bottom bar from reaching PhotoSwipe's gesture handler
        const bottomBar = this.pswpElement.querySelector('.pswp__bottom-bar');
        if (bottomBar) {
          ['mousedown', 'touchstart', 'pointerdown'].forEach(evtName => {
            bottomBar.addEventListener(evtName, (evt) => evt.stopPropagation());
          });
        }

        let pswp = new PhotoSwipe(this.pswpElement, PhotoSwipeUI_Default, this.items, this.pswpOptions);
        const _this = this;

        pswp.listen('firstUpdate', function () {
          pswp.listen('parseVerticalMargin', function (item) {
            item.vGap = {
              top: 50,
              bottom: 140
            };
          });
        });

        // Reset any accidental parent scroll caused by loop transition
        const scrollWrap = this.pswpElement.querySelector('.pswp__scroll-wrap');
        const resetScroll = function () {
          if (scrollWrap) scrollWrap.scrollLeft = 0;
          _this.pswpElement.scrollLeft = 0;
        };

        pswp.listen('beforeChange', function () {
          const index = pswp.getCurrentIndex();
          _this.syncPswpThumbnails(index);
          resetScroll();
          // Sync product slideshow with lightbox
          if (_this.flkty) {
            _this.flkty.select(index);
          }
        });

        const thumbsInner = this.pswpElement.querySelector('.pswp__thumbs-inner');
        if (thumbsInner) {
          thumbsInner.addEventListener('click', function handler(evt) {
            evt.stopPropagation();
            const thumb = evt.target.closest('.pswp__thumb');
            if (thumb) {
              pswp.goTo(parseInt(thumb.dataset.index, 10));
              resetScroll();
            }
          });
        }

        pswp.listen('close', function () {
          const container = _this.pswpElement.querySelector('.pswp__thumbs-inner');
          if (container) container.innerHTML = '';
        });

        pswp.init();
        this.syncPswpThumbnails(this.pswpOptions.index);
      }
      e.preventDefault();
    }
  }
  customElements.define('product-slider', ProductSlider);
}

/**
 *  @class
 *  @function ProductForm
 */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.sticky = this.dataset.sticky;
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      this.form.querySelector('[name=id]').disabled = false;
      if (!this.sticky) {
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }
      this.cartNotification = document.querySelector('cart-notification');
      this.body = document.body;

      this.hideErrors = this.dataset.hideErrors === 'true';
    }
    // MPC: required-Size (E1) — the submit buttons associated with THIS form. The sticky button
    // sits outside the form and is bound to it by its form attribute (cleanup 2.4).
    mpcButtons() {
      return Array.from(document.querySelectorAll('.single-add-to-cart-button')).filter((button) => button.form === this.form);
    }

    // MPC: required-Size (E1) — completeness is decided by this section's MAIN picker(s) only.
    // The sticky twin (data-sticky="1", product-add-to-cart-sticky.liquid:22) is a slave that
    // updateOther syncs; its modal moves itself to document.body, so a combined-listing
    // switchProduct can leave an orphaned twin (and an unsynced new one) behind — neither may be
    // able to refuse a valid selection. A WebKit bfcache restore is still caught, because it
    // resets the main picker's radios too.
    mpcRequiredFieldsets() {
      return Array.from(document.querySelectorAll(`variant-selects[data-section="${this.dataset.section}"]:not([data-sticky]) fieldset[data-required="true"]`));
    }

    // MPC: required-Size (E1) — the refusal UI is painted on EVERY picker of the section, main and
    // sticky, because either can be the one on screen.
    mpcErrorFieldsets() {
      return Array.from(document.querySelectorAll(`variant-selects[data-section="${this.dataset.section}"] fieldset[data-required="true"]`));
    }

    // MPC: required-Size (E1) — v3's refusal UI, scoped to this section's pickers. Both the main
    // and the sticky picker are marked, because either can be the one on screen.
    mpcShowSizeError(submitter) {
      const message = window.theme?.variantStrings?.selectSize || 'Please select a size';
      const fieldsets = this.mpcErrorFieldsets();

      if (!fieldsets.length) {
        // MPC: no required option here — the id is blank for another reason (a resolution that
        // failed), so say something generic instead of asking for a size the product has not got.
        this.handleErrorMessage(window.theme?.variantStrings?.unavailable || 'Unavailable');
        return;
      }

      fieldsets.forEach((fieldset) => {
        fieldset.classList.add('has-error');
        if (fieldset.querySelector('.variant-error')) return;
        const errorMessage = document.createElement('div');
        errorMessage.className = 'variant-error';
        errorMessage.setAttribute('role', 'alert');
        errorMessage.setAttribute('aria-live', 'assertive');
        errorMessage.textContent = message;
        fieldset.appendChild(errorMessage);
      });

      // MPC: scroll the picker the shopper is looking at — the sticky modal's when the sticky
      // button submitted, otherwise the first one that is actually visible.
      const modal = submitter ? submitter.closest('modal-dialog') : null;
      const target = fieldsets.find((fieldset) => (modal ? modal.contains(fieldset) : fieldset.offsetParent !== null)) || fieldsets[0];
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    onSubmitHandler(evt) {
      evt.preventDefault();

      if (!this.form.reportValidity()) {
        return;
      }

      // MPC: required-Size (E1) — fail closed. A blank id means there is no complete, resolved
      // selection, whatever the buttons look like. This is also the path Enter in the quantity
      // field takes, which no button's disabled attribute covers.
      const variantInput = this.form.querySelector('input[name="id"]');
      if (!variantInput || !variantInput.value) {
        this.mpcShowSizeError(evt.submitter);
        return;
      }

      // MPC: required-Size (E1) — a committed id is not proof the size is still chosen. A WebKit
      // bfcache restore resets the autocomplete="off" radios and fires no change event, so the id
      // outlives the selection. Read the live controls here, where no event ordering can be
      // assumed: an empty required fieldset refuses, blanks the id and repaints through the
      // picker's normal entry point, so the button cannot read "Add to cart" over a blank id.
      const incomplete = this.mpcRequiredFieldsets().some((fieldset) => {
        const select = fieldset.querySelector('select');
        return select ? !select.value : !fieldset.querySelector('input:checked');
      });
      if (incomplete) {
        variantInput.value = '';
        document.querySelectorAll(`variant-selects[data-section="${this.dataset.section}"]`).forEach((picker) => {
          if (picker.isConnected) picker.onVariantChange();
        });
        this.mpcShowSizeError(evt.submitter);
        return;
      }

      // MPC: E9 / cleanup 2.4 — one in-flight add per form. The vendor's per-button check returns
      // from the forEach callback only, so the fetch below ran on every repeated click.
      if (this.form.dataset.mpcSubmitting === 'true') return;
      this.form.dataset.mpcSubmitting = 'true';

      // MPC: cleanup 2.4 — scope by form association, not the whole document.
      const submitButtons = this.mpcButtons();

      submitButtons.forEach((submitButton) => {
        if (submitButton.classList.contains('loading')) return;
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
      });

      this.handleErrorMessage();


      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };


      let formData = new FormData(this.form);

      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message
            });
            if (response.status === 422) {
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true
              }));
            }
            this.handleErrorMessage(response.description);
            return;
          }

          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          // MPC: E9 — release the form-level lock on every path, success or failure.
          this.form.dataset.mpcSubmitting = '';
          submitButtons.forEach((submitButton) => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
          if (document.querySelector('.product-add-to-cart-sticky-modal')) {
            document.querySelector('.product-add-to-cart-sticky-modal').removeAttribute('open');
          }
        });
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer-inner' // MPC: D P1 — #Cart-Drawer IS .cart-drawer; the old selector only ever hit the || fallback
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        // MPC: D P1 — also skip a section whose body did not come back, instead of throwing mid-loop.
        if (!document.getElementById(section.id) || !parsedState.sections?.[section.section]) {
          return;
        }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      }));



      let product_drawer = document.getElementById('Product-Drawer'),
        search_drawer = document.getElementById('Search-Drawer'),
        cart_drawer = document.getElementById('Cart-Drawer');

      if (search_drawer && OverlayManager.isOpen(search_drawer)) {
        OverlayManager.close(search_drawer);
      }
      if (cart_drawer) {
        cart_drawer.notesToggle();
        cart_drawer.discountToggle();
        cart_drawer.updateFreeShipping();
      }
      if (product_drawer && product_drawer.contains(this)) {
        OverlayManager.close(product_drawer);
        if (cart_drawer) {
          OverlayManager.open(cart_drawer, ['open-cart']);
        }
      } else if (cart_drawer) {
        OverlayManager.open(cart_drawer, ['open-cart']);
        dispatchCustomEvent('cart-drawer:open');
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}

/**
 *  @class
 *  @function ProductAddToCartSticky
 */
if (!customElements.get('product-add-to-cart-sticky')) {
  class ProductAddToCartSticky extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.setupObservers();
      this.drawer = document.getElementById(this.dataset.target);
      this.addEventListener('click', this.onClick.bind(this));
    }
    onClick() {
      this.drawer.show();
    }
    setupObservers() {
      let _this = this,
        observer = new IntersectionObserver(function (entries) {
          entries.forEach((entry) => {
            if (entry.target === footer) {
              if (entry.intersectionRatio > 0) {
                _this.classList.remove('sticky--visible');
              } else if (entry.intersectionRatio == 0 && _this.formPassed) {
                _this.classList.add('sticky--visible');
              }
            }
            if (entry.target === form) {
              let boundingRect = form.getBoundingClientRect();

              if (entry.intersectionRatio === 0 && window.scrollY > (boundingRect.top + boundingRect.height)) {
                _this.formPassed = true;
                _this.classList.add('sticky--visible');
              } else if (entry.intersectionRatio === 1) {
                _this.formPassed = false;
                _this.classList.remove('sticky--visible');
              }
            }
          });
        }, {
          threshold: [0, 1]
        }),
        form = document.getElementById(`product-form-${this.dataset.section}`),
        footer = document.getElementById('footer');
      _this.formPassed = false;
      observer.observe(form);
    }
  }

  customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}

if (typeof addIdToRecentlyViewed !== "undefined") {
  addIdToRecentlyViewed();
}